import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import moment from 'moment-timezone';

import { PageHeader } from '../../../components/PageHeader';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../components/PageHeader/Button';
import { DATE_TIME_SPACE_12_UPPERCASE } from '../../../constants';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import { ActionWidget, ActionButton } from '../../../components/ActionWidget';
import { notificationActions } from '../../../components/Notification/redux/actions';
import { openModalAction } from '../../../actions/modal';
import actions from './redux/actions';
import {
  GET_SUPERUSER_HEALTH_ALERT_RULE_SUBSCRIPTIONS,
  DELETE_SUPERUSER_HEALTH_ALERT_SUBSCRIPTION,
} from './redux/constants';
import Strings from '../../../Strings';
import { showStatus } from '../../../utils';

function RulesDetails(props) {
  const { 
    getRuleSubscriptions, 
    deleteSubscription, 
    showNotification, 
    onNavigate, 
    openEditSubscriptionModal,
    match: { params: { ruleId } }
  } = props;
  
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const tableRef = useRef(null);

  const fetchSubscriptions = () => {
    setIsLoading(true);
    
    getRuleSubscriptions(ruleId).then(resp => {
      if (resp.type === `${GET_SUPERUSER_HEALTH_ALERT_RULE_SUBSCRIPTIONS}/result`) {
        setSubscriptions(resp.response || []);
      }
      setIsLoading(false);
    }).catch(() => {
      setIsLoading(false);
    });
    
    tableRef?.current?.resetSelectedRows();
  };

  useEffect(() => {
    if (ruleId) {
      fetchSubscriptions();
    }
  }, [ruleId]);

  // Actions
  const onDeleteSubscriptions = (rows = selectedRows) => {
    rows.forEach(row => {
      deleteSubscription(row.id).then(resp => {
        if (resp.type !== `${DELETE_SUPERUSER_HEALTH_ALERT_SUBSCRIPTION}/result`) {
          showNotification('Failed to delete subscription', 5000, true);
        }
        fetchSubscriptions();
      });
    });
  };

  const onAddSubscription = () => {
    openEditSubscriptionModal({ 
      subscription: null,
      ruleId,
      onSuccess: fetchSubscriptions,
    });
  };

  const onBack = () => {
    onNavigate('/superuser-health/alerts/rules');
  };

  const buttons = [
    {
      icon: 'edit',
      onClick: (_, row) => openEditSubscriptionModal({ subscription: row, ruleId, onSuccess: fetchSubscriptions }),
      text: Strings.edit,
    },
    {
      icon: 'delete',
      onClick: (_, row) => onDeleteSubscriptions([row]),
      text: Strings.delete,
    },
  ];

  const columns = React.useMemo(() => [
    columnHelper.accessor('name', {
      header: 'Subscription Label',
      cell: info => info.getValue() || '-',
      size: 200,
    }),
    columnHelper.accessor('linked_user', {
      header: 'Recipient User',
      accessorFn: row => row.user_id ? row.resolved_email : '',
      cell: info => info.getValue() || '-',
      size: 180,
    }),
    columnHelper.accessor('email_override', {
      header: 'Email Override',
      cell: info => info.getValue() || '-',
      size: 180,
    }),
    columnHelper.accessor('phone_number_override', {
      header: 'Phone Override',
      cell: info => info.getValue() || '-',
      size: 150,
    }),
    columnHelper.accessor('resolved_email', {
      header: 'Resolved Email',
      cell: info => info.getValue() || '-',
      size: 150,
    }),
    columnHelper.accessor('resolved_phone_number', {
      header: 'Resolved Phone',
      cell: info => info.getValue() || '-',
      size: 150,
    }),
    columnHelper.accessor('sms_enabled', {
      header: 'SMS',
      cell: info => showStatus(info.getValue()),
      size: 80,
    }),
    columnHelper.accessor('email_enabled', {
      header: 'Email',
      cell: info => showStatus(info.getValue()),
      size: 80,
    }),
    columnHelper.accessor('active', {
      header: 'Active',
      cell: info => showStatus(info.getValue()),
      size: 80,
    }),   
    columnHelper.accessor('created_at', {
      header: 'Created',
      cell: info => moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE),
      size: 180,
    }),
  ], []);

  // Render
  const pageHeaderLeft = (
    <Button class="back" onClick={onBack} title="Back" />
  );

  const pageHeaderRight = (
    <Button class={HEADER_BUTTON_DARK_BLUE} onClick={onAddSubscription} title="Add Subscription" />
  );

  return (
    <>
      <PageHeader left={pageHeaderLeft} right={pageHeaderRight} />
      <Table3
        isLoading={isLoading}
        name="rule-subscriptions"
        data={subscriptions}
        columns={columns}
        onRowSelectionChange={setSelectedRows}
        enableRowSelection
        enableColumnFiltering
        ref={tableRef}
        noScroll
        pinFirstColumn
        buttons={buttons}
      />

      <ActionWidget show={selectedRows?.length > 0}>
        <ActionButton 
          img="action-delete" 
          tooltiptext="Delete Subscriptions" 
          text="Delete" 
          action={onDeleteSubscriptions} 
        />
      </ActionWidget>
    </>
  );
}

RulesDetails.propTypes = {
  getRuleSubscriptions: PropTypes.func.isRequired,
  deleteSubscription: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  openEditSubscriptionModal: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
};

const mapDispatchToProps = dispatch => ({
  getRuleSubscriptions: ruleId => dispatch(actions.getRuleSubscriptions(ruleId)),
  deleteSubscription: subscriptionId => dispatch(actions.deleteSubscription(subscriptionId)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
  onNavigate: path => dispatch(push(path)),
  openEditSubscriptionModal: data => dispatch(openModalAction('edit-subscription-modal', data)),
});

export default connect(null, mapDispatchToProps)(RulesDetails);
